import { Container, RenderLayer } from "pixi.js";

export const LAYER_NAMES = [
  "ground",
  "belowObject",
  "entity",
  "aboveObject",
  "fx",
  "nameplate",
  "debug",
] as const;

export type LayerName = (typeof LAYER_NAMES)[number];

const HUD_LAYERS: ReadonlySet<LayerName> = new Set(["nameplate", "debug"]);

/**
 * Manages the seven canonical render layers + dual scene roots.
 *
 *  worldRoot  — transformed by Camera2D (pan/zoom)
 *  hudRoot    — screen-space, never transformed
 *
 * Each layer name maps to:
 *  - a `RenderLayer` (Pixi v8) which controls render order
 *  - a `Container` parent under worldRoot/hudRoot for hierarchy/lifecycle
 *
 * To draw something on a layer:
 *   layers.attach("entity", sprite)
 * The sprite keeps its transform from the world container but renders
 * in the chosen layer's order.
 */
export class RenderLayers {
  readonly worldRoot: Container;
  readonly hudRoot: Container;
  private layers = new Map<LayerName, RenderLayer>();
  private parents = new Map<LayerName, Container>();

  constructor() {
    this.worldRoot = new Container({ label: "worldRoot" });
    this.hudRoot = new Container({ label: "hudRoot" });

    for (const name of LAYER_NAMES) {
      const layer = new RenderLayer();
      const parent = new Container({ label: `parent:${name}` });
      const target = HUD_LAYERS.has(name) ? this.hudRoot : this.worldRoot;
      target.addChild(parent);
      this.layers.set(name, layer);
      this.parents.set(name, parent);
    }
  }

  attachAll(stage: Container): void {
    stage.addChild(this.worldRoot);
    stage.addChild(this.hudRoot);
    // RenderLayers must be added in render order — siblings of the content roots.
    for (const name of LAYER_NAMES) {
      const target = HUD_LAYERS.has(name) ? this.hudRoot : this.worldRoot;
      target.addChild(this.getLayer(name));
    }
  }

  getLayer(name: LayerName): RenderLayer {
    const l = this.layers.get(name);
    if (!l) throw new Error(`unknown render layer: ${name}`);
    return l;
  }

  getParent(name: LayerName): Container {
    const c = this.parents.get(name);
    if (!c) throw new Error(`unknown render layer: ${name}`);
    return c;
  }

  attach(name: LayerName, displayObject: Container): void {
    this.getParent(name).addChild(displayObject);
    this.getLayer(name).attach(displayObject);
  }

  detach(name: LayerName, displayObject: Container): void {
    this.getLayer(name).detach(displayObject);
    if (displayObject.parent) displayObject.parent.removeChild(displayObject);
  }
}
